import React, { useState } from "react";
import { Sensor } from "../../dtos/sensor";
import { Checkbox, Button, Grid } from "@material-ui/core";
import { ChartSettings } from "../../dtos/chartSettings";
import { makeStyles } from "@material-ui/core/styles";
import SettingsIcon from "@material-ui/icons/Settings";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import AddEditSensors from "./addEditSensorsModal";
import { useRouteMatch } from "react-router-dom";
import { useApi } from "../apiService";
import { SensorAlarmStatus } from "../../enums/sensorAlarmStatus";
import { THEME_VARIABLES } from "../../themeColors";
import moment from "moment";

export const SensorSelector = ({
  sensors,
  chartSettings,
  updateSelection,
  refreshSensorData
}: {
  sensors: Sensor[];
  chartSettings: ChartSettings[];
  updateSelection: (sensorId: number, sensorType: number) => void;
  refreshSensorData: () => void;
}) => {
  const match = useRouteMatch<{ fieldId: string | undefined }>();
  const fieldId = match.params.fieldId ? parseInt(match.params.fieldId) : 0;
  const [selectedSensor, setSelectedSensor] = useState<Sensor | null>(null);
  const [showAddNew, setShowAddNew] = useState(false);
  const { sensorsApi } = useApi();
  const classes = useStyles();

  const addUpdateSensor = (sensor: Sensor | null) => {
    setSelectedSensor(sensor);
    setShowAddNew(true);
  };
  const addSensor = () => {
    setShowAddNew(true);
  };
  const finishOperation = () => {
    setShowAddNew(false);
    setSelectedSensor(null);
    refreshSensorData();
  };
  return (
    <div>
      <div className={classes.addSensorContainer}>
        <div>
          <h2>Sensors</h2>
        </div>
        <div></div>
        <div>
          <Button onClick={addSensor} color="primary">
            <AddCircleOutlineIcon />
          </Button>
        </div>
      </div>
      <div className={classes.sensorList}>
        {sensors.map((s, i) => {
          return (
            <div className={classes.sensorContainer} key={i}>
              <Grid
                container
                spacing={0}
                alignItems="center"
                justify="space-around"
              >
                <Grid item xs={2}>
                  <div>
                    <Checkbox
                      onChange={() => updateSelection(s.id, s.type || 0)}
                      checked={
                        !!chartSettings.find(c => c.sensorId === s.id) || false
                      }
                    />
                  </div>
                </Grid>
                <Grid item xs={3}>
                  <div>
                    <div className={classes.sensorTitle}>{s.name}</div>
                    {s.name !== "Average" && (
                      <div className={classes.sensorSubtitle}>
                        {s.serial} CH{s.channel}
                      </div>
                    )}
                  </div>
                </Grid>
                <Grid item xs={5}>
                  <div>
                    <StatusIndicator
                      isAverage={s.name === "Average"}
                      status={s.alarmStatus || 0}
                      currentEngData={s.currentEngData || 0}
                    />
                    <div>
                      {s.lastUpdatedTimestamp
                        ? moment(s.lastUpdatedTimestamp).format(
                          "M/D/YYYY, h:mm:ss a"
                        )
                        : "No update time"}
                    </div>
                  </div>
                </Grid>
                <Grid item xs={2}>
                  <div>
                    <Button onClick={() => addUpdateSensor(s)}>
                      <SettingsIcon />
                    </Button>
                  </div>
                </Grid>
              </Grid>
            </div>
          );
        })}
      </div>
      <AddEditSensors
        isVisible={showAddNew}
        close={finishOperation}
        sensor={selectedSensor}
        fieldId={fieldId}
        addSensor={sensorsApi.postOne}
        updateSensor={sensorsApi.putOne}
        deleteSensor={sensorsApi.delete}
      />
    </div>
  );
};

const StatusIndicator = ({
  status,
  currentEngData,
  isAverage
}: {
  status: SensorAlarmStatus;
  currentEngData: number;
  isAverage: boolean;
}) => {
  const classes = useStyles();

  return (
    <div>
      <div className={`${classes.dot} ${SensorAlarmStatus[status]}`}></div>
      <div className={`${classes.status} ${SensorAlarmStatus[status]}`}>
        {SensorAlarmStatus[status]
          ? SensorAlarmStatus[status].replace(/([A-Z])/g, " $1").trim() +
          (status === 0 && !isAverage
            ? ""
            : " (" + Number(currentEngData.toFixed(2)) + ")")
          : ""}
      </div>
    </div>
  );
};

const useStyles = makeStyles({
  dot: {
    height: "12px",
    width: "12px",
    borderRadius: "50%",
    display: "inline-block",
    verticalAlign: "middle",
    "&.AlarmHigh": {
      backgroundColor: THEME_VARIABLES.COLOR_RED
    },
    "&.AlarmLow": {
      backgroundColor: THEME_VARIABLES.COLOR_RED
    },
    "&.Normal": {
      backgroundColor: THEME_VARIABLES.COLOR_GREEN
    },
    "&.NoAlarmData": {
      backgroundColor: THEME_VARIABLES.COLOR_DARK_GRAY
    },
    "&.Offline": {
      backgroundColor: THEME_VARIABLES.COLOR_GRAY_1
    }
  },
  status: {
    display: "inline-block",
    marginLeft: "5px",
    "&.AlarmHigh": {
      color: THEME_VARIABLES.COLOR_RED
    },
    "&.AlarmLow": {
      color: THEME_VARIABLES.COLOR_RED
    },
    "&.Normal": {
      color: THEME_VARIABLES.COLOR_GREEN
    },
    "&.NoAlarmData": {
      color: THEME_VARIABLES.COLOR_DARK_GRAY
    }
  },
  sensorList: {
    maxHeight: "500px",
    marginTop: "15px"
  },
  sensorContainer: {
    border: "2px solid #e1e1e1",
    borderRadius: "8px",
    flex: "auto",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    height: "50px",
    marginBottom: "10px"
  },
  addSensorContainer: {
    flex: "auto",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    height: "50px",
    marginBottom: "10px"
  },
  averageCheckbox: {
    display: "flex",
    alignItems: "center"
  },
  sensorTitle: {},
  sensorSubtitle: {
    color: "#888",
    fontSize: "80%"
  },
  addSensorButton: {
    display: "inline-block"
  }
});
